import {
  Autocomplete,
  AutocompleteItem,
  Button,
  Checkbox,
  Chip,
  cn,
  DatePicker,
  AutocompleteItem as HeroAutoCompleteItem,
  Input,
  Link,
  Select,
  SelectItem,
  Textarea,
} from "@heroui/react";

import type { Key } from "@react-types/shared";

import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";

import {
  Control,
  Controller,
  FieldValues,
  Path,
  useFormContext,
  useWatch,
} from "react-hook-form";

import { CalendarDate } from "@internationalized/date";

import { FieldOptions, MultiFieldOptions } from "@/features/Edit/types.ts";
import { formatNumericInput } from "@/lib/utils";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useAutocompleteItems } from "@/hooks/useAutoCompleteItems";
import { useSelectOptions } from "@/hooks/useSelectOptions";
import { useTranslation as useTableTranslations } from "@/hooks/useTranslation";
import { useTranslation as useI18n } from "react-i18next";
import { getAssetClassTranslation, mergeFieldOptions } from "./utils";
import { useAutoLoadList } from "@/hooks/useAutoLoadList";
import { FiltersArg } from "../FilterTable/types";
import { SelectFieldWithSection } from "./SelectFieldWithSection";
import type { FieldProps } from "./types";

type CheckboxFieldProps<T extends FieldValues> = {
  name: Path<T>;
  label: string;
  control: Control<T>;
} & FieldProps;

const CheckboxField = <T extends FieldValues>({
  name,
  label,
  control,
  fieldOptions,
  multiFieldOptions,
  className,
}: CheckboxFieldProps<T>) => {
  const { t } = useI18n();
  const translatedLabel = t(label, { defaultValue: label });
  const opt = mergeFieldOptions(name, fieldOptions, multiFieldOptions);

  if (opt?.isVisible === false) return undefined;

  return (
    <div className={className}>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Checkbox
            size="sm"
            isSelected={field.value ?? false}
            onChange={field.onChange}
            isDisabled={opt?.isDisabled}
          >
            {translatedLabel}
          </Checkbox>
        )}
      />
    </div>
  );
};

export type SelectOption = { value: string; label: string };

type SelectFieldProps<T extends FieldValues> = {
  name: Path<T>;
  label: string;
  control: Control<T>;
  isRequired?: boolean;
  allow_empty_option?: boolean;
  isMulti?: boolean;

  // STATIC
  options?: SelectOption[] | { [key: string]: string };

  // REMOTE
  base_url?: string;
} & FieldProps;

const SelectField = <T extends FieldValues>({
  name,
  label,
  control,
  isRequired = false,
  fieldOptions,
  multiFieldOptions,
  className,
  allow_empty_option = false,
  isMulti = false,
  options,
  base_url,
}: SelectFieldProps<T>) => {
  const { t } = useI18n();
  const translatedLabel = t(label, { defaultValue: label });
  const opt = mergeFieldOptions(name, fieldOptions, multiFieldOptions);
  if (opt?.isVisible === false) return undefined;

  const { options: remoteOptions } = useSelectOptions(base_url);

  const formattedOptions = useMemo<SelectOption[]>(() => {
    const base = base_url
      ? remoteOptions
      : !options
        ? []
        : Array.isArray(options)
          ? options
          : Object.entries(options).map(([value, label]) => ({
              value,
              label,
            }));

    return [...base].sort((a, b) =>
      a.label.localeCompare(b.label, "fr", { sensitivity: "base" }),
    );
  }, [options, base_url, remoteOptions]);

  return (
    <div className={className}>
      <FormField
        control={control}
        name={name}
        render={({ field }) => {
          const selectedKeys: Set<Key> = isMulti
            ? new Set(
                Array.isArray(field.value)
                  ? field.value.map((v: any) => String(v))
                  : [],
              )
            : field.value == null || field.value === ""
              ? allow_empty_option
                ? new Set<Key>([""])
                : new Set<Key>()
              : new Set<Key>([String(field.value)]);

          return (
            <FormItem>
              <FormControl>
                <Select
                  label={translatedLabel}
                  variant="bordered"
                  labelPlacement="outside"
                  radius="full"
                  size="sm"
                  isRequired={isRequired}
                  isDisabled={opt?.isDisabled}
                  selectionMode={isMulti ? "multiple" : "single"}
                  selectedKeys={selectedKeys}
                  onSelectionChange={(keys) => {
                    if (keys === "all") {
                      const all = formattedOptions.map((o) => o.value);
                      field.onChange(isMulti ? all : (all[0] ?? null));
                      return;
                    }

                    const values = Array.from(keys).map(String);
                    const selectedValue = values[0] ?? null;
                    // Only send null if we are allowed to, otherwise send the value (even if empty)
                    const finalValue =
                      allow_empty_option && !selectedValue
                        ? null
                        : selectedValue;
                    field.onChange(isMulti ? values : finalValue);
                  }}
                >
                  <>
                    {!isMulti && allow_empty_option ? (
                      <SelectItem key="" textValue="">
                        {" "}
                      </SelectItem>
                    ) : null}

                    {formattedOptions.map((o) => (
                      <SelectItem
                        key={o.value}
                        textValue={t(o.label, { defaultValue: o.label })}
                      >
                        {t(o.label, { defaultValue: o.label })}
                      </SelectItem>
                    ))}
                  </>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          );
        }}
      />
    </div>
  );
};

type InputFieldProps<T extends FieldValues> = {
  name: Path<T>;
  label: string;
  control: Control<T>;
  variant?: "bordered" | "flat" | "faded" | "underlined";
  isRequired?: boolean;
  type?: "text" | "number";
  endContent?: React.ReactNode;
  allowZero?: boolean;
  allowNegative?: boolean;
  allowDecimal?: boolean;
  valueMapper?: (value: any) => string;
  capitalize?: boolean;
} & FieldProps;

const InputField = <T extends FieldValues>({
  name,
  label,
  control,
  variant = "bordered",
  isRequired = false,
  type = "text",
  fieldOptions,
  multiFieldOptions,
  className,
  endContent,
  allowZero = true,
  allowNegative = true,
  allowDecimal = true,
  valueMapper,
  capitalize = false,
}: InputFieldProps<T>) => {
  const { t } = useI18n();
  const translatedLabel = t(label, { defaultValue: label });
  const opt = mergeFieldOptions(name, fieldOptions, multiFieldOptions);

  if (opt?.isVisible === false) return undefined;
  const changeOnDisable =
    opt?.changeOnDisable != undefined ? opt.changeOnDisable : true;
  const fieldVariant =
    fieldOptions?.isDisabled && changeOnDisable ? "underlined" : variant;

  return (
    <div className={className}>
      <FormField
        control={control}
        name={name}
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <Input
                label={translatedLabel}
                radius="full"
                variant={fieldVariant}
                labelPlacement="outside"
                size="sm"
                type="text"
                endContent={endContent}
                onInput={
                  type === "number"
                    ? (e) => {
                        const input = e.target as HTMLInputElement;
                        input.value = formatNumericInput(
                          input.value,
                          allowZero,
                          allowNegative,
                          allowDecimal,
                        );
                      }
                    : undefined
                }
                className="min-w-36"
                isRequired={isRequired}
                isDisabled={opt?.isDisabled}
                isReadOnly={opt?.isReadOnly}
                {...field}
                onChange={(e) => {
                  const raw = (e?.target as HTMLInputElement)?.value ?? "";
                  const next =
                    type === "number"
                      ? raw === ""
                        ? null
                        : raw
                      : capitalize
                        ? raw.toUpperCase()
                        : raw;
                  field.onChange(next);
                }}
                value={
                  valueMapper ? valueMapper(field.value) : (field.value ?? "")
                }
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};

type AutocompleteFieldProps<T extends FieldValues> = {
  name: Path<T>;
  label: string;
  options: { key?: string; value: string }[];
  control: Control<T>;
  isRequired?: boolean;
  returnSelectedKey?: boolean;
} & FieldProps;

const AutocompleteField = <T extends FieldValues>({
  name,
  label,
  options,
  control,
  isRequired = false,
  returnSelectedKey = false,
  className,
  fieldOptions,
  multiFieldOptions,
}: AutocompleteFieldProps<T>) => {
  const { t } = useI18n();
  const translatedLabel = t(label, { defaultValue: label });
  const opt = mergeFieldOptions(name, fieldOptions, multiFieldOptions);

  if (opt?.isVisible === false) return undefined;

  return (
    <div className={className}>
      <FormField
        control={control}
        name={name}
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <Autocomplete
                label={translatedLabel}
                aria-label={translatedLabel}
                variant="bordered"
                radius="full"
                size="sm"
                labelPlacement="outside"
                placeholder=" "
                defaultItems={options}
                isRequired={isRequired}
                inputValue={
                  returnSelectedKey
                    ? (options.find((option) => option.key === field.value)
                        ?.value ??
                      field.value ??
                      "")
                    : (field.value ?? "")
                }
                isDisabled={opt?.isDisabled}
                selectedKey={
                  returnSelectedKey
                    ? options.find((option) => option.key === field.value)?.key
                    : options.find((opt) => opt.value === field.value)?.value
                }
                onSelectionChange={(value) =>
                  field.onChange(value == null || value === "" ? null : value)
                }
                onInputChange={(value) =>
                  field.onChange(value == "" ? null : value)
                }
                onClear={() => field.onChange(null)}
              >
                {(item) => (
                  <HeroAutoCompleteItem
                    key={returnSelectedKey ? item.key : item.value}
                    textValue={String(item.value)}
                  >
                    {item.value}
                  </HeroAutoCompleteItem>
                )}
              </Autocomplete>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};

type DatePickerFieldProps<T extends FieldValues> = {
  name: Path<T>;
  label: string;
  control: Control<T>;
  isRequired?: boolean;
} & FieldProps;

const getCurrentDate = () => {
  const today = new Date();
  return new CalendarDate(
    today.getFullYear(),
    today.getMonth() + 1,
    today.getDate(),
  );
};

const defaultPlaceholderValueStatic = new CalendarDate(2024, 1, 1);

const DatePickerField = <T extends FieldValues>({
  name,
  label,
  control,
  isRequired,
  className,
  fieldOptions,
  multiFieldOptions,
}: DatePickerFieldProps<T>) => {
  const { t } = useI18n();
  const translatedLabel = t(label, { defaultValue: label });
  const opt = mergeFieldOptions(name, fieldOptions, multiFieldOptions);
  const [placeholderValue, setPlaceholderValue] = useState(
    defaultPlaceholderValueStatic,
  );

  const uuid = useId();
  const [isOpen, setIsOpen] = useState(false);
  const popoverId = `date-picker-popover-${uuid}`;

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) return;
    const button = ref.current.nextElementSibling as HTMLButtonElement | null;
    if (!button) return;

    const handleButtonHover = () => {
      if (!isOpen) setPlaceholderValue(getCurrentDate());
    };

    const handleButtonLeave = () => {
      if (!isOpen) setPlaceholderValue(defaultPlaceholderValueStatic);
    };

    button.addEventListener("mouseenter", handleButtonHover);
    button.addEventListener("mouseleave", handleButtonLeave);

    return () => {
      button.removeEventListener("mouseenter", handleButtonHover);
      button.removeEventListener("mouseleave", handleButtonLeave);
    };
  }, [isOpen]);

  useEffect(() => {
    const checkPopoverClosed = () => {
      const popoverElement = document.getElementById(popoverId);
      const popoverIsOpen = popoverElement !== null;

      if (!popoverIsOpen && isOpen) {
        setIsOpen(false);
        setPlaceholderValue(defaultPlaceholderValueStatic);
      }
    };

    const observer = new MutationObserver(() => {
      checkPopoverClosed();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [isOpen, popoverId]);

  if (opt?.isVisible === false) return undefined;

  return (
    <div className={className}>
      <FormField
        control={control}
        name={name}
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <DatePicker
                label={translatedLabel}
                ref={ref}
                variant="bordered"
                labelPlacement="outside"
                size="sm"
                radius="full"
                className="min-w-36"
                isRequired={isRequired}
                isDisabled={opt?.isDisabled}
                value={field.value ?? null}
                granularity="day"
                shouldForceLeadingZeros={false}
                onChange={(date) => {
                  field.onChange(date);
                }}
                onOpenChange={(open: boolean) => {
                  setIsOpen(open);
                }}
                popoverProps={{
                  id: popoverId,
                }}
                placeholderValue={placeholderValue as any}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};

type TextareaFieldProps<T extends FieldValues> = {
  name: Path<T>;
  label: string;
  control: Control<T>;
  variant?: "bordered" | "flat" | "faded" | "underlined";
  isRequired?: boolean;
  isDisabled?: boolean;
  placeholder?: string;
  defaultValue?: string;
} & FieldProps;

const TextareaField = <T extends FieldValues>({
  name,
  label,
  control,
  variant = "bordered",
  isRequired = false,
  placeholder = "",
  defaultValue = "",
  className,
  fieldOptions,
  multiFieldOptions,
}: TextareaFieldProps<T>) => {
  const { t } = useI18n();
  const translatedLabel = t(label, { defaultValue: label });
  const translatedPlaceholder = placeholder
    ? t(placeholder, { defaultValue: placeholder })
    : placeholder;
  const opt = mergeFieldOptions(name, fieldOptions, multiFieldOptions);

  if (opt?.isVisible === false) return undefined;

  return (
    <div className={className}>
      <FormField
        control={control}
        name={name}
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <Textarea
                label={translatedLabel}
                labelPlacement="outside"
                size="sm"
                radius="full"
                variant={variant}
                isRequired={isRequired}
                isDisabled={opt?.isDisabled}
                isReadOnly={opt?.isReadOnly}
                defaultValue={defaultValue}
                placeholder={translatedPlaceholder}
                {...field}
              />
            </FormControl>
          </FormItem>
        )}
      />
    </div>
  );
};

type TAutocompleteLabelFieldProps<T extends FieldValues> = {
  name: Path<T>;
  label: string;
  options: { value: string; label: string }[];
  control: Control<T>;
  isRequired?: boolean;
  className?: string;
  fieldOptions?: FieldOptions;
  multiFieldOptions?: MultiFieldOptions;
};

const AutocompleteLabelField = <T extends FieldValues>({
  name,
  label,
  options,
  control,
  isRequired = false,
  className,
  fieldOptions,
  multiFieldOptions,
}: TAutocompleteLabelFieldProps<T>) => {
  const { t } = useI18n();
  const translatedLabel = t(label, { defaultValue: label });
  const opt = mergeFieldOptions(name, fieldOptions, multiFieldOptions);

  if (opt?.isVisible === false) return undefined;

  return (
    <div className={className}>
      <FormField
        control={control}
        name={name}
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <Autocomplete
                label={translatedLabel}
                aria-label={translatedLabel}
                variant="bordered"
                radius="full"
                size="sm"
                isDisabled={opt?.isDisabled}
                labelPlacement="outside"
                className={cn("min-w-36", className)}
                isRequired={isRequired}
                defaultSelectedKey={field.value}
                inputValue={field.value ?? ""}
                onInputChange={(value) => {
                  field.onChange(value);
                }}
                onSelectionChange={(key) => {
                  if (key)
                    field.onChange(
                      options.find((option) => option.value === key)?.value ||
                        key,
                    );
                }}
                onClear={() => field.onChange(null)}
              >
                {options.map((option) => {
                  const label = t(option.label, { defaultValue: option.label });
                  return (
                    <AutocompleteItem key={option.value} textValue={label}>
                      {label}
                    </AutocompleteItem>
                  );
                })}
              </Autocomplete>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};

type TAutocompleteAsycFieldProps<T extends FieldValues> = Omit<
  AutocompleteFieldProps<T>,
  "options"
> & {
  base_url: string;
  id_column?: string;
  showId?: boolean;
  column_name?: string;
  options?: {
    base_id_url?: string;
    translateKey?: string;
    client?: string;
    table_name?: string;
    language?: string;
    translations?: Record<string, Record<string, string>>;
    allowEmptyQuery?: boolean;
    keepDuplicates?: boolean;
    filters?: FiltersArg;
    autoSelectSingle?: boolean;
    itemLink?: string;
  };
};

/**
 * Async autocomplete field.
 *
 * - `name` comes from AutocompleteFieldProps and is the React Hook Form field
 * that stores the selected item object. Typically used to write code like
 *     const nameValue = form.getValues("name")?.text ?? "";
 *
 * - `base_url` is the API route prefix used to fetch autocomplete items. Example:
 * "n1/n1_portfolios" becomes ".../n1/n1_portfolios/autocomplete/{column_name}".
 *
 * - `id_column` is an optional form field updated with the selected item's id.
 * Example: selecting a portfolio label can also set "n1_portfolio_id".
 *
 * - `column_name` controls which backend column is searched by the autocomplete
 * endpoint. If omitted, `name` is used as the searched column. Use it when the
 * form field name differs from the API column, for example
 * name="n1_portfolio_name" with column_name="name".
 */
const AutocompleteAsyncField = <T extends FieldValues>({
  name,
  label,
  control,
  base_url,
  id_column,
  column_name,
  isRequired = false,
  className,
  showId = true,
  options = {},
}: TAutocompleteAsycFieldProps<T>) => {
  const { t } = useI18n();
  const translatedLabel = t(label, { defaultValue: label });

  const raw = useWatch({ control, name });
  const [inputValue, setInputValue] = useState("");
  const isSelectionChange = useRef(false);
  const { setValue, clearErrors, getValues } = useFormContext();
  const [isOpen, setIsOpen] = useState(false);

  const allowEmptyQuery =
    options.allowEmptyQuery != undefined ? options.allowEmptyQuery : true;
  const keepDuplicates =
    options.keepDuplicates != undefined ? options.keepDuplicates : true;
  const autoSelectSingle =
    options.autoSelectSingle != undefined ? options.autoSelectSingle : false;

  const actualColumnName = column_name || name;

  const {
    items,
    hasMore,
    isLoading,
    onLoadMore,
    onQueryChange,
    cancelPending,
  } = useAutocompleteItems(base_url, actualColumnName, {
    allowEmptyQuery,
    keepDuplicates,
    filters: options.filters,
  });

  const listboxRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const handleLoadMoreClick = async () => {
    if (isLoading || !hasMore) return;
    await onLoadMore();
    requestAnimationFrame(() => {
      const scroller = listboxRef.current;
      if (!scroller) return;

      const remaining =
        scroller.scrollHeight - (scroller.scrollTop + scroller.clientHeight);
      const stillAtBottom = remaining <= 4;

      if (stillAtBottom) {
        const nudge = Math.ceil(scroller.clientHeight * 0.33);
        scroller.scrollTop = Math.max(0, scroller.scrollTop - nudge);
      }
    });
  };

  useAutoLoadList({
    listboxRef,
    sentinelRef,
    isOpen,
    hasMore,
    isLoading,
    onLoadMore,
    deps: [items.length],
  });

  const showBottomButton =
    (allowEmptyQuery || (inputValue && inputValue.length >= 1)) &&
    (hasMore || isLoading);

  useEffect(() => {
    const initialText = typeof raw === "string" ? raw : (raw?.text ?? "");
    if ((initialText ?? "") !== (inputValue ?? "")) {
      isSelectionChange.current = true;
      setInputValue(initialText);
    }
  }, [raw, inputValue]);

  useEffect(() => {
    if (isSelectionChange.current) {
      isSelectionChange.current = false;
      return;
    }

    const timer = setTimeout(() => {
      if (inputValue && inputValue.length >= 1) {
        onQueryChange(inputValue);
      }
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [inputValue, onQueryChange]);

  useEffect(() => {
    if (!autoSelectSingle) return;
    if (!items || items.length !== 1) return;

    const current = getValues(name as Path<T>) as any;
    const hasValue =
      current &&
      (typeof current === "string"
        ? current.trim() !== ""
        : current?.id != null);

    if (hasValue) return;

    const q = (inputValue ?? "").trim().toLowerCase();
    const only = items[0];
    const onlyText = (only?.text ?? "").toLowerCase();
    if (q && q !== onlyText) return;

    isSelectionChange.current = true;
    setValue(name, only as any);
    if (id_column) {
      setValue(id_column, only.id);
    }
    setInputValue(only.text || "");
    clearErrors(name as any);
  }, [
    items.length,
    autoSelectSingle,
    inputValue,
    name,
    id_column,
    setValue,
    getValues,
    clearErrors,
    items,
  ]);

  const inputRef = useRef<HTMLButtonElement>(null);

  const { translations: fetchedTranslations } = useTableTranslations({
    client: options.client ?? "",
    table_name: options.table_name ?? "",
    language: options.language ?? "fr",
  });

  const emptyContent = isLoading
    ? t("autocomplete.loading")
    : allowEmptyQuery
      ? t("autocomplete.noResults")
      : inputValue && inputValue.length >= 1
        ? t("autocomplete.noResults")
        : t("autocomplete.typeToSearch");

  const loadMoreLabel = isLoading
    ? t("autocomplete.loadingEllipsis")
    : hasMore
      ? t("autocomplete.loadMore")
      : "—";

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormControl>
            <Autocomplete
              label={translatedLabel}
              aria-label={translatedLabel}
              variant="bordered"
              radius="full"
              size="sm"
              labelPlacement="outside"
              className={cn("min-w-36", className)}
              isRequired={isRequired}
              isLoading={isLoading}
              items={items}
              inputValue={inputValue || ""}
              selectedKey={field.value?.id?.toString()}
              selectorButtonProps={{ ref: inputRef }}
              scrollRef={listboxRef}
              listboxProps={{
                ref: listboxRef,
                classNames: {
                  base: "max-h-80 overflow-auto",
                },
                emptyContent,
                bottomContent: showBottomButton ? (
                  <div className="p-2 flex justify-center w-full">
                    <div
                      ref={sentinelRef}
                      className="flex justify-center w-full"
                    >
                      <Button
                        size="sm"
                        variant="flat"
                        className="w-full"
                        isDisabled={isLoading || !hasMore}
                        onPress={handleLoadMoreClick}
                      >
                        {loadMoreLabel}
                      </Button>
                    </div>
                  </div>
                ) : null,
              }}
              onOpenChange={(open, reason) => {
                if (reason === "input" && inputRef.current) {
                  inputRef.current.click();
                }
                setIsOpen(open);
                if (!open) {
                  cancelPending();
                  return;
                }
                if ((inputValue ?? "").trim().length > 0) return;
                onQueryChange(inputValue || "");
              }}
              endContent={
                <div className="flex flex-row items-center justify-center gap-1 py-0.5">
                  {field.value?.asset_class_2 ? (
                    <Chip
                      size="sm"
                      variant="flat"
                      color="default"
                      className="!m-0"
                    >
                      {getAssetClassTranslation(
                        field.value?.asset_class_2,
                        "asset_class_2",
                        fetchedTranslations,
                      )}
                    </Chip>
                  ) : null}
                  {field.value?.id && showId ? (
                    <Chip
                      size="sm"
                      variant="flat"
                      color="default"
                      className="!m-0"
                    >
                      {field.value?.id}
                    </Chip>
                  ) : null}
                  {options?.itemLink ? (
                    <Button
                      href={options.itemLink}
                      variant="light"
                      color="primary"
                      showAnchorIcon
                      as={Link}
                      className="bg-transparent shadow-none p-0 min-w-0 h-6 w-6"
                    />
                  ) : null}
                </div>
              }
              onInputChange={(value) => {
                setInputValue(value);
                if (value) {
                  const currentId = field.value?.id;
                  field.onChange({ text: value, id: currentId });
                } else {
                  field.onChange(null);
                }
              }}
              onSelectionChange={(key) => {
                if (!key) return;
                if (!isOpen) return;

                const selectedItem = items.find(
                  (item) => item.id.toString() === key,
                );

                if (!selectedItem) return;

                isSelectionChange.current = true;
                field.onChange(selectedItem);
                if (id_column) {
                  setValue(id_column, selectedItem.id);
                }
                setInputValue(selectedItem.text);
                clearErrors(name);
              }}
              onClose={() => {
                const valueExists = items.some(
                  (item) =>
                    item.text.toLowerCase() === inputValue.toLowerCase(),
                );

                if (
                  !isSelectionChange.current &&
                  (!valueExists || inputValue === "")
                ) {
                  field.onChange(null);
                  if (id_column) {
                    setValue(id_column, null);
                  }
                }
              }}
              onClear={() => {
                field.onChange(null);
                if (id_column) {
                  setValue(id_column, null);
                }
                setInputValue("");
              }}
            >
              {(item) => (
                <HeroAutoCompleteItem
                  key={item.id.toString()}
                  textValue={item.text}
                >
                  <div className="flex items-center justify-between w-full">
                    <span>{item.text}</span>
                    {showId ? (
                      <Chip
                        size="sm"
                        variant="flat"
                        color="default"
                        className="ml-2 relative top-0 bottom-0"
                      >
                        {options?.base_id_url ? (
                          <a
                            href={`${options.base_id_url}/${item.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {t("autocomplete.id", { id: item.id })}
                          </a>
                        ) : (
                          t("autocomplete.id", { id: item.id })
                        )}
                      </Chip>
                    ) : null}
                  </div>
                </HeroAutoCompleteItem>
              )}
            </Autocomplete>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export {
  AutocompleteAsyncField,
  AutocompleteField,
  AutocompleteLabelField,
  CheckboxField,
  DatePickerField,
  InputField,
  SelectField,
  SelectFieldWithSection,
  TextareaField,
};

export type { TSections } from "./types";
